import React, { memo, useEffect, useMemo, useState } from "react";

import { Backdrop } from "@mui/material";

import { customHistory } from "App";
import { Spinner } from "react-bootstrap";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";

import SwalAlertComp from "components/SwalAlert";

import {
    AddFunnelSteps,
    AddLandingPageSteps,
    CommonLandingPageSteps,
    EditFunnelSteps,
    EditLandingPageSteps,
} from "./AddEditLandingPage.types";
import { LandingPageWizardShell } from "./AddEditLandingPage.styled";
import LandingPageSidebar from "./components/Sidebar";
import StepManager from "./components/StepManager";

const isEncoded = (str) => {
    try {
        atob(str);
        return true;
    } catch (error) {
        return false;
    }
};

function base64Decode(str) {
    try {
        return decodeURIComponent(escape(atob(str)));
    } catch (error) {
        return atob(str); // Fallback to simple atob if decoding fails
    }
}
function jsonToFormData(json, formData = new FormData(), parentKey = "") {
    for (const key in json) {
        if (!json.hasOwnProperty(key)) continue;

        const value = json[key];
        const fullKey = parentKey ? `${parentKey}[${key}]` : key;

        // Handle File objects as binary FormData (for images, videos, etc.)
        if (value instanceof File || value instanceof Blob) {
            formData.append(fullKey, value);
        } else if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value)
        ) {
            // Recursively process nested objects
            jsonToFormData(value, formData, fullKey);
        } else if (Array.isArray(value)) {
            // Handle arrays
            value.forEach((item, index) => {
                if (item instanceof File || item instanceof Blob) {
                    // Handle File objects in arrays as binary
                    formData.append(`${fullKey}[${index}]`, item);
                } else if (typeof item === "object" && item !== null) {
                    jsonToFormData(item, formData, `${fullKey}[${index}]`);
                } else {
                    formData.append(`${fullKey}[${index}]`, item);
                }
            });
        } else {
            // Append primitive values
            formData.append(fullKey, value);
        }
    }

    return formData;
}
export function useUnsavedChangesPrompt(hasUnsavedChanges) {
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        let isUnblocked = false;
        const unblock = customHistory.block((tx) => {
            if (hasUnsavedChanges) {
                const confirmLeave = window.confirm(
                    "You have unsaved changes. Are you sure you want to leave?",
                );
                if (confirmLeave) {
                    if (!isUnblocked) {
                        isUnblocked = true;
                        unblock(); // MUST unblock before retrying
                    }
                    tx.retry();
                }
                // else: stay on page (do not retry)
            } else {
                if (!isUnblocked) {
                    isUnblocked = true;
                    unblock(); // safe unblock even if already safe
                }
                tx.retry();
            }
        });

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            if (!isUnblocked) {
                unblock(); // cleanup if never navigated
            }
        };
    }, [hasUnsavedChanges]);
}
const AddEditLandingPage = ({
    language,
    code,
    appDetails,
    templates,
    customSources,
    landingPageDetails,
    lastAddedDraftLandingPage,
    gamificationTemplates,
    dispatchGetTemplates,
    isLandingPageCreated,
    isLandingPageFormLoading,

    dispatchGenerateCode,
    dispatchPreviewLandingPage,
    dispatchPutUpdateLandingPage,
    dispatchPublishLandingPage,
    dispatchClearState,
    dispatchGetGamificationTemplates,
    dispatchLeadCustomSources,
}) => {
    const navigate = useNavigate();
    const landingPageID = useParams()?.id;
    const location = useLocation();
    const isCloneMode = useMemo(
        () => location.pathname.includes("clone"),
        [location.pathname],
    );
    const isEditMode = useMemo(() => !!landingPageID, [landingPageID]);
    const isDraft = useMemo(
        () => !landingPageDetails?.is_published,
        [landingPageDetails],
    );
    // const isDraft = true;
    const [searchParams] = useSearchParams();
    const trackerId = searchParams.get("id");
    const [isStepDirty, setIsStepDirty] = useState(false);

    const [step, setStep] = useState(
        isEditMode
            ? EditLandingPageSteps.OVERVIEW
            : AddLandingPageSteps.GENERAL,
    );
    const [completedSteps, setCompletedSteps] = useState(
        isEditMode || isCloneMode
            ? [
                  ...Object.keys(
                      landingPageDetails?.template_id === 7
                          ? EditFunnelSteps
                          : EditLandingPageSteps,
                  ).filter(
                      (item) =>
                          item !== EditLandingPageSteps.PREVIEW_AND_LAUNCH,
                  ),
              ]
            : [],
    );

    const [form, setForm] = useState({});

    const selectedTemplate = useMemo(() => {
        return form[CommonLandingPageSteps.TEMPLATE]?.template_id;
    }, [form]);

    const selectedSourceApiID = useMemo(() => {
        return form[CommonLandingPageSteps.CONNECTIONS]?.lead_source_api_id;
    }, [form]);
    const selectedTemplatePopulated = useMemo(() => {
        return templates?.data?.find(
            (template) => template?.id === selectedTemplate,
        );
    }, [selectedTemplate, templates]);

    const prepareStepsForFrontend = (steps) => {
        return steps.map((step) => {
            // Handle both formats: step.attributes (backend) and step.config (legacy frontend)
            const attributes = step.attributes || step.config || {};

            const commonConfig = {
                id: step.step_id,
                title: step.title,
                description: step.description || "",
                type: step.type,
                optional: step.optional !== undefined ? step.optional : false,
                order: step.order !== undefined ? step.order : 0,
                image: step.image !== undefined ? step.image : null,
                image_url: step.image_url !== undefined ? step.image_url : "",
                ...(step.mapped_field_id && {
                    mapped_field_id: step.mapped_field_id,
                }),
            };

            const handlers = [
                {
                    predicate: () => step.type === "radio-button",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            options: attributes.options || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "input",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.placeholder && {
                                placeholder: attributes.placeholder,
                            }),
                            ...(attributes.input_type && {
                                input_type: attributes.input_type,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "otp",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.otp_length && {
                                otp_length: attributes.otp_length,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "button",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.button_text && {
                                button_text: attributes.button_text,
                            }),
                            ...(attributes.button_text_color && {
                                button_text_color: attributes.button_text_color,
                            }),
                            ...(attributes.button_color && {
                                button_color: attributes.button_color,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "multiselect",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            options: attributes.options || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "star_rating",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.number_of_stars && {
                                number_of_stars: attributes.number_of_stars,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "nps_rating",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.min !== undefined && {
                                min: attributes.min,
                            }),
                            ...(attributes.max !== undefined && {
                                max: attributes.max,
                            }),
                            ...(attributes.min_label && {
                                min_label: attributes.min_label,
                            }),
                            ...(attributes.max_label && {
                                max_label: attributes.max_label,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "range_slider",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.min !== undefined && {
                                min: attributes.min,
                            }),
                            ...(attributes.max !== undefined && {
                                max: attributes.max,
                            }),
                            ...(attributes.step_size && {
                                step_size: attributes.step_size,
                            }),
                            ...(attributes.unit && {
                                unit: attributes.unit,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "image_carousel",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            images: attributes.images || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "grid_selector",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            grid_items:
                                attributes.grid_items?.map((item) => ({
                                    ...item,
                                })) || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "grid_selector_multiple",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            grid_items: attributes.grid_items || [],
                        },
                    }),
                },
                {
                    predicate: () =>
                        step.type === "categorical_slider" ||
                        step.type === "slider",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.min !== undefined && {
                                min: attributes.min,
                            }),
                            ...(attributes.max !== undefined && {
                                max: attributes.max,
                            }),
                            ...(attributes.mid !== undefined && {
                                mid: attributes.mid,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "file_upload",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.file_type && {
                                file_type: attributes.file_type,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "processing_step",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            processing_steps: attributes.processing_steps || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "checkbox",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.label && {
                                label: attributes.label,
                            }),
                            checked:
                                attributes.checked === "1" ||
                                attributes.checked === true
                                    ? true
                                    : attributes.checked === "0" ||
                                        attributes.checked === false
                                      ? false
                                      : false,
                        },
                    }),
                },
            ];

            const handler = handlers.find((handler) => handler.predicate());
            return handler
                ? handler.handler()
                : { ...commonConfig, attributes: {} };
        });
    };

    const prepareStepsForBackend = (steps) => {
        return steps.map((step, index) => {
            // Handle both formats: step.config (frontend) and step.attributes (backend)
            const attributes = step.attributes || step.config || {};

            const commonConfig = {
                step_id: index + 1,
                title: step.title,
                ...(step.description && { description: step.description }),
                type: step.type,
                optional: step.optional !== undefined ? step.optional : false,
                order: step.order !== undefined ? step.order : index,
                ...(step.image && { image: step.image }),
                ...(step.image_url && { image_url: step.image_url }),
                ...(step.mapped_field_id && {
                    mapped_field_id: step.mapped_field_id,
                }),
            };

            const handlers = [
                {
                    predicate: () => step.type === "radio-button",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            options: attributes.options || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "input",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.placeholder && {
                                placeholder: attributes.placeholder,
                            }),
                            ...(attributes.input_type && {
                                input_type: attributes.input_type,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "otp",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.otp_length && {
                                otp_length: attributes.otp_length,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "button",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.button_text && {
                                button_text: attributes.button_text,
                            }),
                            ...(attributes.button_text_color && {
                                button_text_color: attributes.button_text_color,
                            }),
                            ...(attributes.button_color && {
                                button_color: attributes.button_color,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "multiselect",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            options: attributes.options || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "star_rating",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.number_of_stars && {
                                number_of_stars: attributes.number_of_stars,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "nps_rating",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.min !== undefined && {
                                min: attributes.min,
                            }),
                            ...(attributes.max !== undefined && {
                                max: attributes.max,
                            }),
                            ...(attributes.min_label && {
                                min_label: attributes.min_label,
                            }),
                            ...(attributes.max_label && {
                                max_label: attributes.max_label,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "range_slider",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.min !== undefined && {
                                min: attributes.min,
                            }),
                            ...(attributes.max !== undefined && {
                                max: attributes.max,
                            }),
                            ...(attributes.step_size && {
                                step_size: attributes.step_size,
                            }),
                            ...(attributes.unit && {
                                unit: attributes.unit,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "image_carousel",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            images: attributes.images || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "grid_selector",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            grid_items:
                                attributes.grid_items?.map(
                                    ({ description, ...item }) => ({
                                        ...item,
                                        ...(description ? { description } : {}),
                                        ...(item.image
                                            ? { image: item.image }
                                            : {}),
                                        ...(item.image_url
                                            ? { image_url: item.image_url }
                                            : {}),
                                    }),
                                ) || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "grid_selector_multiple",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            grid_items:
                                attributes.grid_items?.map(
                                    ({ description, ...item }) => ({
                                        ...item,
                                        ...(description ? { description } : {}),
                                        ...(item.image
                                            ? { image: item.image }
                                            : {}),
                                        ...(item.image_url
                                            ? { image_url: item.image_url }
                                            : {}),
                                    }),
                                ) || [],
                        },
                    }),
                },
                {
                    predicate: () =>
                        step.type === "categorical_slider" ||
                        step.type === "slider",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.min !== undefined && {
                                min: attributes.min,
                            }),
                            ...(attributes.max !== undefined && {
                                max: attributes.max,
                            }),
                            ...(attributes.mid !== undefined && {
                                mid: attributes.mid,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "file_upload",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.file_type && {
                                file_type: attributes.file_type,
                            }),
                        },
                    }),
                },
                {
                    predicate: () => step.type === "processing_step",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            processing_steps: attributes.processing_steps || [],
                        },
                    }),
                },
                {
                    predicate: () => step.type === "checkbox",
                    handler: () => ({
                        ...commonConfig,
                        attributes: {
                            ...(attributes.label && {
                                label: attributes.label,
                            }),
                            checked: attributes.checked ? "1" : "0",
                        },
                    }),
                },
            ];

            const handler = handlers.find((handler) => handler.predicate());
            return handler ? handler.handler() : commonConfig;
        });
    };

    const onSubmitHandler = async (stepData) => {
        const {
            GENERAL,
            TEMPLATE,
            CONNECTIONS,
            PAYWALL_INTEGRATION,
            PAGE_CONTENT,
            FUNNEL,
        } = stepData;
        const data = {
            ...GENERAL,
            ...TEMPLATE,
            ...CONNECTIONS,
            ...PAYWALL_INTEGRATION,
            ...PAGE_CONTENT,
            ...FUNNEL,
        };

        Object.keys(data).forEach((key) => {
            if (
                data[key] === undefined ||
                data[key] === null ||
                data[key] === ""
            )
                delete data[key];
        });

        const {
            logo,
            video,
            cta,
            logo_url,
            video_url,
            app_type,
            mmp_one_link,
            is_published,
            page_url, // discard
            mmp,
            deleted_at,
            paywall_activated,
            redirect_time,
            steps,
            funnelSteps,
            html_description,
            gamification_type,
            gamification_items,
            funnelType,
            android_app_id, // Remove - backend expects applications[android] instead
            apple_app_id, // Remove - backend expects applications[apple] instead
            ...rest
        } = data;
        (paywall_activated === false || paywall_activated === "0") &&
            delete rest?.paywall_integration;
        rest.paywall_activated = paywall_activated ? "1" : "0";

        // Ensure android_app_id and apple_app_id are not sent to backend
        // Backend expects applications[android] and applications[apple] instead
        delete rest.android_app_id;
        delete rest.apple_app_id;
        function base64Encode(str) {
            return btoa(unescape(encodeURIComponent(str)));
        }
        const formData = jsonToFormData(rest);
        formData.append("redirect_time", redirect_time);
        cta?.description &&
            formData.append("cta[description]", cta?.description);
        html_description &&
            formData.append("html_description", base64Encode(html_description));
        mmp && formData.append("mmp", mmp);
        logo && formData.append("logo", logo);
        logo_url && formData.append("logo_url", logo_url);
        video && formData.append("video", video);
        video_url && formData.append("video_url", video_url);
        funnelType &&
            selectedTemplatePopulated?.name === "funnel" &&
            formData.append("template_type_id", funnelType === "chat" ? 2 : 1);
        if (selectedTemplatePopulated?.name === "funnel" && funnelSteps) {
            const stepsData = { steps: prepareStepsForBackend(funnelSteps) };
            jsonToFormData(stepsData, formData);
        }
        if (selectedTemplatePopulated?.name === "gamification") {
            formData.append("gamification_type", gamification_type);
            formData.append(
                "gamification_items",
                jsonToFormData(gamification_items),
            );
        }

        (lastAddedDraftLandingPage || isEditMode) &&
            formData.append(
                "id",
                lastAddedDraftLandingPage?.id || landingPageID,
            );
        if (!isEditMode && !lastAddedDraftLandingPage) {
            return await dispatchPreviewLandingPage(formData);
        } else if (isEditMode || lastAddedDraftLandingPage) {
            return await dispatchPutUpdateLandingPage(formData);
        }
    };

    const onPublishHandler = async () => {
        return await dispatchPublishLandingPage({
            id: lastAddedDraftLandingPage?.id || landingPageDetails?.id,
        });
    };

    const onSaveStep = async ({ stepName, data }) => {
        const funnelType = data?.funnel_type;
        const funnel_steps = data?.steps;
        if (
            (stepName === CommonLandingPageSteps.PAGE_CONTENT &&
                selectedTemplate !== 7) ||
            (stepName === AddFunnelSteps.FUNNEL && selectedTemplate === 7)
        ) {
            setIsStepDirty(false);
            const payload =
                stepName === CommonLandingPageSteps.PAGE_CONTENT
                    ? {
                          ...form,
                          [CommonLandingPageSteps.PAGE_CONTENT]: data,
                      }
                    : {
                          ...form,
                          [AddFunnelSteps.FUNNEL]: {
                              funnelType,
                              funnelSteps: funnel_steps,
                          },
                      };
            const response = await onSubmitHandler(payload);
            if (response?.error?.message) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.error?.message ?? "",
                    icon: "error",
                });
                return;
            }
        }
        if (
            stepName === CommonLandingPageSteps.PREVIEW_AND_LAUNCH ||
            (stepName === CommonLandingPageSteps.PREVIEW_AND_LAUNCH && isDraft)
        ) {
            setIsStepDirty(false);
            const response = await onPublishHandler();

            if (response?.error?.message) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.error?.message ?? "",
                    icon: "error",
                });
                return;
            }
        }
        setIsStepDirty(false);
        if (
            form?.[CommonLandingPageSteps.TEMPLATE]?.template_id &&
            data?.template_id &&
            data?.template_id !== selectedTemplate
        ) {
            setForm({
                ...form,
                [CommonLandingPageSteps.TEMPLATE]: data,
            });
            setCompletedSteps([
                ...completedSteps,
                CommonLandingPageSteps.TEMPLATE,
            ]);
        } else {
            setForm({
                ...form,
                [stepName]: data,
            });
            setCompletedSteps([...completedSteps, stepName]);
        }

        const generalSteps = Object.keys(
            isEditMode ? EditLandingPageSteps : AddLandingPageSteps,
        );
        const funnelSteps = isEditMode
            ? Object.keys(EditFunnelSteps)
            : Object.keys(AddFunnelSteps);

        const steps = selectedTemplate === 7 ? funnelSteps : generalSteps;
        const stepID = steps.findIndex((item) => item === stepName);
        setStep(steps[stepID + 1]);
    };
    useEffect(() => {
        appDetails?.data &&
            setForm({
                ...form,
                [CommonLandingPageSteps.PAGE_CONTENT]: {
                    logo_url: appDetails?.data?.logo,
                    title: appDetails?.data?.title,
                    ...(selectedTemplatePopulated?.fields?.find(
                        (field) => field?.name === "description",
                    )
                        ? { description: appDetails?.data?.description }
                        : []),
                    cta: {
                        description: appDetails?.data?.cta,
                    },
                },
            });
    }, [appDetails]);

    useEffect(() => {
        !isEditMode &&
            !isCloneMode &&
            code?.data &&
            setForm({ [CommonLandingPageSteps.GENERAL]: { code: code?.data } });
    }, [code?.data]);
    useEffect(() => {
        if (
            selectedSourceApiID &&
            selectedTemplatePopulated?.name === "funnel" &&
            form[CommonLandingPageSteps.PAGE_CONTENT]?.steps?.length > 0
        ) {
            setForm({
                ...form,
                [CommonLandingPageSteps.PAGE_CONTENT]: {
                    ...form[CommonLandingPageSteps.PAGE_CONTENT],
                    steps: form[
                        CommonLandingPageSteps.PAGE_CONTENT
                    ]?.steps?.map((step) => {
                        delete step?.mapped_field_id;
                        return step;
                    }),
                },
            });
        }
    }, [selectedSourceApiID]);

    useEffect(() => {
        dispatchGetTemplates();

        if (!isEditMode) {
            dispatchGenerateCode();
        }
        if (isEditMode && !landingPageDetails) navigate("/landing-pages");

        return () => dispatchClearState({ clearDetails: true });
    }, []);

    useEffect(() => {
        if (selectedTemplatePopulated?.name === "gamification")
            dispatchGetGamificationTemplates();
        else if (
            selectedTemplatePopulated?.name === "lead" ||
            selectedTemplatePopulated?.name === "funnel"
        )
            dispatchLeadCustomSources();
    }, [selectedTemplatePopulated?.id]);

    useEffect(() => {
        if ((isEditMode || isCloneMode) && landingPageDetails) {
            /**
             * overview eksik
             * publish ve create de id store etme yönlendirme page_url gösterme lazım
             */
            const {
                id,
                name,
                page_url,

                redirect_time,
                lead_source_api_id,
                template_id,
                tracker_id,
                gamification_type,

                code,
                theme,
                created_at,
                updated_at,
                mmp,
                target_link,
                one_link,
                cpp_android,
                cpp_ios,
                steps,
                html_description,
                paywall_activated,
                paywall_integration,
                template_type_id,
                ...rest
            } = landingPageDetails;

            // const redirect_time = 3;
            setForm({
                [EditLandingPageSteps.OVERVIEW]: {
                    page_url,
                },
                [CommonLandingPageSteps.GENERAL]: {
                    name: isCloneMode ? `${name} Clone` : name,
                    code,
                    redirect_time:
                        redirect_time > 2 && redirect_time < 10
                            ? 1
                            : redirect_time,
                },
                [CommonLandingPageSteps.TEMPLATE]: {
                    theme,
                    template_id,
                },
                [CommonLandingPageSteps.CONNECTIONS]: {
                    tracker_id: tracker_id || trackerId,
                    lead_source_api_id,
                    mmp,
                    cpp_android,
                    cpp_ios,
                    gamification_type,

                    ...(landingPageDetails?.apple_app_id ||
                    landingPageDetails?.android_app_id ||
                    landingPageDetails?.target_link ||
                    landingPageDetails?.phone_number
                        ? {
                              applications: {
                                  ...(landingPageDetails?.apple_app_id
                                      ? {
                                            apple: landingPageDetails?.apple_app_id,
                                        }
                                      : {}),
                                  ...(landingPageDetails?.android_app_id
                                      ? {
                                            android:
                                                landingPageDetails?.android_app_id,
                                        }
                                      : {}),
                                  ...(landingPageDetails?.phone_number
                                      ? {
                                            phone_number:
                                                landingPageDetails?.phone_number,
                                        }
                                      : {}),
                              },
                              ...(landingPageDetails?.target_link
                                  ? {
                                        target_link:
                                            landingPageDetails?.target_link,
                                    }
                                  : {}),
                              ...(landingPageDetails?.one_link
                                  ? {
                                        one_link: landingPageDetails?.one_link,
                                    }
                                  : {}),
                              app_type: [
                                  ...(landingPageDetails?.apple_app_id
                                      ? ["apple"]
                                      : []),
                                  ...(landingPageDetails?.android_app_id
                                      ? ["android"]
                                      : []),
                                  ...(landingPageDetails?.target_link
                                      ? ["web"]
                                      : []),
                                  ...(landingPageDetails?.phone_number
                                      ? ["whatsapp"]
                                      : []),
                              ],
                          }
                        : {}),
                },
                [CommonLandingPageSteps.PAYWALL_INTEGRATION]: {
                    paywall_activated: !!paywall_activated,
                    paywall_integration,
                },
                [CommonLandingPageSteps.PAGE_CONTENT]: {
                    ...rest,
                    ...(html_description && {
                        html_description: isEncoded(html_description)
                            ? base64Decode(html_description)
                            : html_description,
                    }),
                },
                [AddFunnelSteps.FUNNEL]: {
                    funnelType: template_type_id === 2 ? "chat" : "web",
                    ...(steps && steps?.length > 0
                        ? { steps: prepareStepsForFrontend(steps) }
                        : {}),
                },
            });
        }
    }, [landingPageDetails]);

    useEffect(() => {
        isLandingPageCreated && navigate("/landing-pages");
    }, [isLandingPageCreated]);

    // Check if templates are loading
    const isTemplatesLoading = templates?.isLoadingList;
    useUnsavedChangesPrompt(isStepDirty);

    return (
        <>
            <Backdrop
                open={!!isTemplatesLoading}
                sx={{
                    color: "black",
                    zIndex: (theme) => 9999999,
                    background: "rgba(255, 255, 255, 0.5)",
                }}
            >
                <Spinner
                    animation="border"
                    role="status"
                    variant="dark"
                    className="p-5 "
                ></Spinner>
            </Backdrop>
            <LandingPageWizardShell
                direction={{ xs: "column", md: "row" }}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    height: { xs: "auto", md: "100dvh" },
                    overflow: { xs: "visible", md: "hidden" },
                }}
            >
                <LandingPageSidebar
                    isDraft={isDraft}
                    step={step}
                    setStep={setStep}
                    completedSteps={completedSteps}
                    isStepDirty={isStepDirty}
                    setIsStepDirty={setIsStepDirty}
                    selectedTemplate={selectedTemplate}
                />
                <StepManager
                    landingPageDetails={landingPageDetails}
                    completedSteps={completedSteps}
                    isDraft={isDraft}
                    step={step}
                    setStep={setStep}
                    setCompletedSteps={setCompletedSteps}
                    form={form}
                    onSave={onSaveStep}
                    setIsStepDirty={setIsStepDirty}
                    pageURL={
                        landingPageDetails?.page_url ||
                        lastAddedDraftLandingPage?.page_url
                    }
                    templates={templates}
                    customSources={customSources}
                    gamificationTemplates={gamificationTemplates}
                />
            </LandingPageWizardShell>
        </>
    );
};

export default memo(AddEditLandingPage);
